import React, { useState, ChangeEvent, useCallback, useEffect } from "react";
import Cropper from "react-easy-crop";

interface MultiImageUploadProps {
  label?: string;
  description?: string;
  files: (File | null)[];
  previewUrls: (string | null)[];
  onFilesChange: (files: (File | null)[]) => void;
  maxImages?: number;
  inputClassName?: string;
  aspect?: number;
}

const MultiImageUpload: React.FC<MultiImageUploadProps> = ({
  label = "Imagens do Empreendimento",
  description,
  files,
  previewUrls,
  onFilesChange,
  maxImages = 3,
  inputClassName,
  aspect = 16 / 9,
}) => {
  const [cropIndex, setCropIndex] = useState<number | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppingSrc, setCroppingSrc] = useState<string | null>(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

  const MIN_ZOOM = 0.1;
  const MAX_ZOOM = 2;

  useEffect(() => {
    if (!croppingSrc) return;
    const img = new Image();
    img.src = croppingSrc;
    img.onload = () => {
      const imgRatio = img.width / img.height;
      const cropRatio = aspect;
      let initialZoom = 1;

      if (imgRatio > cropRatio) {
        initialZoom = 1 / (img.width / img.height / cropRatio);
      } else {
        initialZoom = 1 / (img.height / img.width * cropRatio);
      }

      setZoom(Math.max(MIN_ZOOM, Math.min(initialZoom, MAX_ZOOM)));
      setCrop({ x: 0, y: 0 });
    };
  }, [croppingSrc, aspect]);

  const getCroppedImg = async (imageSrc: string, pixelCrop: any): Promise<Blob> => {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.src = imageSrc;
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
    });

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const canvas = document.createElement("canvas");
    canvas.width = pixelCrop.width * scaleX;
    canvas.height = pixelCrop.height * scaleY;

    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.drawImage(
        image,
        pixelCrop.x * scaleX,
        pixelCrop.y * scaleY,
        pixelCrop.width * scaleX,
        pixelCrop.height * scaleY,
        0,
        0,
        canvas.width,
        canvas.height
      );
    }

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
      }, "image/png");
    });
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const file = e.target.files ? e.target.files[0] : null;
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setCroppingSrc(reader.result as string);
        setCropIndex(index);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
      };
      reader.readAsDataURL(file);
    }
  };

  const onCropComplete = useCallback((_: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleConfirmCrop = useCallback(async () => {
    if (croppingSrc && cropIndex !== null && croppedAreaPixels) {
      const croppedBlob = await getCroppedImg(croppingSrc, croppedAreaPixels);
      const croppedFile = new File([croppedBlob], `cropped-${Date.now()}.png`, { type: "image/png" });

      const newFiles = [...files];
      newFiles[cropIndex] = croppedFile;
      onFilesChange(newFiles);

      setCroppingSrc(null);
      setCropIndex(null);
    }
  }, [croppingSrc, cropIndex, croppedAreaPixels, files, onFilesChange]);

  const handleZoomChange = (newZoom: number) => {
    const delta = newZoom - zoom;
    const smoothFactor = 0.03;
    const smoothDelta = delta * smoothFactor;

    let updatedZoom = zoom + smoothDelta;
    updatedZoom = Math.max(MIN_ZOOM, Math.min(updatedZoom, MAX_ZOOM));

    setZoom(updatedZoom);
    setCrop({ x: 0, y: 0 });
  };

  // Função para criar FormData pronto para backend
  const getFormData = (): FormData => {
    const formData = new FormData();
    files.forEach((file) => {
      if (file) formData.append("imagens", file);
    });
    return formData;
  };

  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden w-full">
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">{label}</h3>
        {description && <p className="mt-1 text-sm text-gray-600">{description}</p>}
      </div>

      <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        {Array.from({ length: maxImages }).map((_, idx) => (
          <div key={idx} className="flex flex-col items-center">
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(e, idx)}
              className={inputClassName || "block w-full border border-gray-300 rounded-md px-2 py-1"}
            />
            {previewUrls[idx] ? (
              <div className="mt-2 border border-gray-300 rounded-md overflow-hidden w-full">
                <img
                  src={previewUrls[idx] as string}
                  alt={`Preview ${idx + 1}`}
                  className="w-full h-32 object-cover"
                />
              </div>
            ) : (
              <p className="mt-2 text-sm text-gray-500">Nenhuma imagem</p>
            )}
          </div>
        ))}
      </div>

      {croppingSrc && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
          <div className="relative w-4/5 h-4/5 bg-white rounded-lg overflow-hidden p-4">
            <Cropper
              image={croppingSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              cropShape="rect"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={handleZoomChange}
              onCropComplete={onCropComplete}
              zoomWithScroll={true}
              minZoom={MIN_ZOOM}
              maxZoom={MAX_ZOOM}
              restrictPosition={false}
            />

            <input
              type="range"
              min={MIN_ZOOM}
              max={MAX_ZOOM}
              step={0.03}
              value={zoom}
              onChange={(e) => handleZoomChange(Number(e.target.value))}
              className="mt-7 w-full z-50 relative"
            />

            <div className="absolute bottom-2 left-2 right-2 flex justify-between items-center">
              <button
                type="button"
                onClick={() => {
                  setCrop({ x: 0, y: 0 });
                  setZoom(1);
                }}
                className="bg-green-500 text-white px-3 py-1 rounded"
              >
                Centralizar
              </button>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    handleConfirmCrop();
                  }}
                  className="bg-blue-500 text-white px-3 py-1 rounded"
                >
                  Confirmar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCroppingSrc(null);
                    setCropIndex(null);
                  }}
                  className="bg-red-500 text-white px-3 py-1 rounded"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiImageUpload;
